import React, { useState, useEffect } from 'react';
import {
  Bell,
  Mail,
  MessageSquare,
  Smartphone,
  Save,
  Loader2,
  AlertCircle,
  Check,
  Clock,
  Calendar
} from 'lucide-react';
import { useCompany } from '../../../../contexts/CompanyContext';
import { settingsService } from '../../../../services/settingsService';

const DIGEST_DAYS = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' }
];

const NotificationPreferencesTab = () => {
  const { currentCompany } = useCompany();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [prefs, setPrefs] = useState({
    // Channels
    email_enabled: true,
    sms_enabled: false,
    push_enabled: true,
    in_app_enabled: true,
    // Events
    job_assignment_notify: true,
    job_update_notify: true,
    job_completion_notify: true,
    payment_notify: true,
    report_notify: true,
    // Digest
    daily_digest_enabled: false,
    weekly_digest_enabled: false,
    digest_time: '08:00',
    digest_day: 'monday'
  });

  useEffect(() => {
    if (currentCompany?.id) {
      loadPreferences();
    }
  }, [currentCompany?.id]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);

      try {
        const { data } = await settingsService.getNotificationPreferences(currentCompany.id);
        if (data) {
          setPrefs(prev => ({ ...prev, ...data }));
        }
      } catch (e) {
        console.warn('Notification preferences not available:', e.message);
        // Use defaults - table might not exist yet
      }
    } catch (err) {
      console.error('Error loading preferences:', err);
      // Don't show error - use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      await settingsService.updateNotificationPreferences(currentCompany.id, prefs);

      setSuccess('Notification preferences saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ checked, onChange, disabled = false }) => (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-orange-500' : 'bg-border'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Notification Preferences</h2>
        <p className="text-text-secondary text-sm mt-1">
          Choose how and when you want to receive notifications
        </p>
      </div>

      {/* Coming Soon Banner */}
      <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
        <div className="flex items-center space-x-2">
          <Bell size={16} className="text-orange-400" />
          <span className="text-orange-400 font-medium">Coming Soon</span>
        </div>
        <p className="text-text-secondary text-sm mt-1">
          Email and push notification delivery is coming soon. Your preferences will be saved and applied once available.
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle size={16} className="text-red-500" />
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center space-x-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <Check size={16} className="text-green-500" />
          <span className="text-green-400 text-sm">{success}</span>
        </div>
      )}

      {/* Notification Channels */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Bell size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Notification Channels</h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <Mail size={18} className="text-text-secondary" />
              <div>
                <p className="text-text-primary text-sm font-medium">Email Notifications</p>
                <p className="text-text-secondary text-xs">Receive notifications via email</p>
              </div>
            </div>
            <Toggle
              checked={prefs.email_enabled}
              onChange={(val) => setPrefs({ ...prefs, email_enabled: val })}
            />
          </div>

          <div className="flex items-center justify-between py-3 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <Bell size={18} className="text-text-secondary" />
              <div>
                <p className="text-text-primary text-sm font-medium">Push Notifications</p>
                <p className="text-text-secondary text-xs">In-app notifications and alerts</p>
              </div>
            </div>
            <Toggle
              checked={prefs.push_enabled}
              onChange={(val) => setPrefs({ ...prefs, push_enabled: val })}
            />
          </div>

          <div className="flex items-center justify-between py-3 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <Smartphone size={18} className="text-text-secondary" />
              <div>
                <p className="text-text-primary text-sm font-medium">In-App Notifications</p>
                <p className="text-text-secondary text-xs">Show notifications in the notification center</p>
              </div>
            </div>
            <Toggle
              checked={prefs.in_app_enabled}
              onChange={(val) => setPrefs({ ...prefs, in_app_enabled: val })}
            />
          </div>

          <div className="flex items-center justify-between py-3">
            <div className="flex items-center space-x-3">
              <MessageSquare size={18} className="text-text-secondary" />
              <div>
                <p className="text-text-primary text-sm font-medium">SMS Notifications</p>
                <p className="text-text-secondary text-xs">Receive text messages for urgent alerts</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="px-2 py-0.5 bg-border/50 text-text-secondary rounded text-xs">Requires Setup</span>
              <Toggle
                checked={prefs.sms_enabled}
                onChange={(val) => setPrefs({ ...prefs, sms_enabled: val })}
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      {/* Event Notifications */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Calendar size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Event Notifications</h3>
        </div>
        <p className="text-text-secondary text-xs mb-4">Choose which events you want to be notified about</p>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-text-primary text-sm">Job Assignments</p>
              <p className="text-text-secondary text-xs">When you are assigned to a new job</p>
            </div>
            <Toggle
              checked={prefs.job_assignment_notify}
              onChange={(val) => setPrefs({ ...prefs, job_assignment_notify: val })}
            />
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-text-primary text-sm">Job Updates</p>
              <p className="text-text-secondary text-xs">When a job you're involved with is modified</p>
            </div>
            <Toggle
              checked={prefs.job_update_notify}
              onChange={(val) => setPrefs({ ...prefs, job_update_notify: val })}
            />
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-text-primary text-sm">Job Completions</p>
              <p className="text-text-secondary text-xs">When a job is marked as completed</p>
            </div>
            <Toggle
              checked={prefs.job_completion_notify}
              onChange={(val) => setPrefs({ ...prefs, job_completion_notify: val })}
            />
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-text-primary text-sm">Payment Updates</p>
              <p className="text-text-secondary text-xs">Payment confirmations and updates</p>
            </div>
            <Toggle
              checked={prefs.payment_notify}
              onChange={(val) => setPrefs({ ...prefs, payment_notify: val })}
            />
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-text-primary text-sm">Report Submissions</p>
              <p className="text-text-secondary text-xs">When reports are submitted for review</p>
            </div>
            <Toggle
              checked={prefs.report_notify}
              onChange={(val) => setPrefs({ ...prefs, report_notify: val })}
            />
          </div>
        </div>
      </div>

      {/* Digest Preferences */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Clock size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Email Digests</h3>
        </div>
        <p className="text-text-secondary text-xs mb-4">Receive a summary of notifications instead of individual emails</p>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-text-primary text-sm">Daily Digest</p>
              <p className="text-text-secondary text-xs">Receive a daily summary email</p>
            </div>
            <Toggle
              checked={prefs.daily_digest_enabled}
              onChange={(val) => setPrefs({ ...prefs, daily_digest_enabled: val })}
            />
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-text-primary text-sm">Weekly Digest</p>
              <p className="text-text-secondary text-xs">Receive a weekly summary email</p>
            </div>
            <Toggle
              checked={prefs.weekly_digest_enabled}
              onChange={(val) => setPrefs({ ...prefs, weekly_digest_enabled: val })}
            />
          </div>

          {(prefs.daily_digest_enabled || prefs.weekly_digest_enabled) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border/50">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Digest Time</label>
                <input
                  type="time"
                  value={prefs.digest_time}
                  onChange={(e) => setPrefs({ ...prefs, digest_time: e.target.value })}
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                />
              </div>
              {prefs.weekly_digest_enabled && (
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Weekly Digest Day</label>
                  <select
                    value={prefs.digest_day}
                    onChange={(e) => setPrefs({ ...prefs, digest_day: e.target.value })}
                    className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                  >
                    {DIGEST_DAYS.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-text-primary rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center"
        >
          {saving ? (
            <>
              <Loader2 size={16} className="animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} className="mr-2" />
              Save Preferences
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default NotificationPreferencesTab;
